import React from "react";
import { Box } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { Attribute } from "../../../new-reports/types/newReportTypes";

type Props = {
  attributes: Attribute[] | undefined;
  label: string;
  // Grid columns (only used when layout is "grid")
  gridColumns?: number;
  // Optional font size overrides
  fontSizes?: {
    sectionTitle?: string;
    attributeLabel?: string;
    attributeValue?: string;
  };
};

const ServiceAttributesSection = ({
  attributes,
  label,
  gridColumns = 1,
  fontSizes,
}: Props) => {
  const theme: Theme = useTheme();

  // Default font sizes (from ServicePage)
  const defaultFontSizes = {
    sectionTitle: "11px",
    attributeLabel: "9px",
    attributeValue: "9px",
  };

  // Merge with provided overrides
  const sizes = { ...defaultFontSizes, ...fontSizes };

  if (!attributes || attributes.length === 0) {
    return null;
  }

  return (
    <Box sx={styles.section}>
      <Box component="p" sx={styles.sectionTitle(theme, sizes.sectionTitle)}>
        {label}
      </Box>
      <Box
        sx={
          gridColumns > 1
            ? styles.attributesBoxGrid(theme, gridColumns)
            : styles.attributesBoxInline(theme)
        }
      >
        {attributes.map((attribute, idx) => (
          <Box key={idx} sx={styles.attributeRow(sizes.attributeLabel)}>
            <Box component="span" sx={styles.attributeLabel(theme)}>
              {`${attribute.name} : `}
            </Box>
            <Box component="span" sx={styles.attributeValue(theme)}>
              {`${attribute.value} ${attribute.unit}`}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ServiceAttributesSection;

const styles = {
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  sectionTitle: (theme: Theme, fontSize: string) => ({
    fontFamily: "var(--font-Regular)",
    fontSize,
    lineHeight: "normal",
    color: theme.colorTokens.N[90],
    margin: 0,
  }),
  // Inline layout styles (ServicePage)
  attributesBoxInline: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "12px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  }),
  // Grid layout styles (ServiceViewModal)
  attributesBoxGrid: (theme: Theme, columns: number) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "12px",
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: "12px 12px", // row gap, column gap
  }),
  attributeRow: (fontSize: string) => ({
    fontSize,
    lineHeight: "14px",
    display: "flex",
    gap: "4px",
  }),
  attributeLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    color: theme.colorTokens.N[80],
    whiteSpace: "nowrap" as "nowrap",
  }),
  attributeValue: (theme: Theme) => ({
    fontFamily: "var(--font-Light)",
    color: theme.colorTokens.N[90],
  }),
};
